import { FASTElement, attr, customElement, observable } from '@microsoft/fast-element';
import { EditUserModalTemplate as template } from './edit-user-modal.template';
import { EditUserModalStyles as styles } from './edit-user-modal.styles';
import { Modal, Multiselect } from '@genesislcap/foundation-zero';
import { Select, TextField } from '@microsoft/fast-foundation';
import { Connect, Message, logger } from '@genesislcap/foundation-comms';
import { IUserService } from '../../../../services/user.service';
import { ICompany, IValidProfileByCompanyRole, ValidProfiles } from '../../../../types';
import { updateArray } from '../../../../utils/dataserver';
import { notifyError } from '../../../../utils/notify';
import { ICompanyService } from '../../../../services/company.service';
import { PROFILE_RIGHTS } from '../../../../utils';
import { COMPANY_ROLE } from '../../companys/edit';
import { Profile } from '../../../../utils/profiles';

const name = 'edit-user-modal';

@customElement({
  name,
  template,
  styles,
})
export class EditUserModal extends FASTElement {
  //This modal is a custom version of the modal that appears in the platform's user management grid
  //There is a possibility that this modal could differ from the platform one in the future

  @IUserService userService!: IUserService;
  @Connect private connect: Connect;
  @ICompanyService companyService: ICompanyService;

  public modal!: Modal;
  public profileMulti!: Multiselect;
  public usernameTxt!: TextField;
  public emailTxt!: TextField;
  public firstNameTxt!: TextField;
  public lastNameTxt!: TextField;
  public passwordTxt!: TextField;
  public companyElement!: Select;

  @attr show: boolean = false;
  @attr onCloseCallback: () => void;
  @attr data;
  @attr event;

  @observable title = '';
  @observable width: string = '390px';
  @observable editing: boolean = false;
  @observable isFormValid: boolean;
  @observable companyList: ICompany[] = [];
  @observable isCascadeAdmin: boolean = false;
  @observable isAdmin: boolean = false;

  public allProfiles: any[] = [];
  public allProfilesFiltered: any[] = [];
  public allProfilesFilteredByCompanyRole: any[] = [];
  private allValidProfiles: ValidProfiles[] = [];
  private validProfileByCompanyRole: Array<IValidProfileByCompanyRole> = [];

  connectedCallback() {
    super.connectedCallback();
    this.getAllProfiles();
    this.getAllValidProfiles();
    this.profileMulti.all = false;
    this.getAllProjects();
    this.isAdmin =
      this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.ADMIN_VISIBILITY) &&
      this.companyService.getCurrentCompany().COMPANY_ROLE === COMPANY_ROLE.CASCADE_ADMIN;
  }

  private getAllProfiles() {
    this.allProfiles = [];
    this.userService.getAllProfiles().subscribe((response) => {
      const resultList = response.ROW ?? null;
      if (resultList) {
        this.allProfiles = updateArray(
          resultList.map((x) => ({
            PROFILE_NAME: x.PROFILE_NAME,
            IS_PRIMARY: x.IS_PRIMARY,
          })),
          this.allProfiles,
        );
        this.filterProfileOptions();
      } else {
        this.allProfiles = [];
      }
    });
  }

  private getAllProjects() {
    this.companyService.getAllCompanies(`IS_SPV_FLAG != true`).subscribe({
      next: (streamMessage: Message) => {
        const companiesResultList = streamMessage.ROW ?? null;
        this.companyList = updateArray(companiesResultList, this.companyList);
      },
      error: (error: any) => {
        logger.error(error);
      },
    });
  }

  public submit() {
    let {
      STATUS,
      ONLINE,
      ADDRESS_LINE1: ADDRESS_LINE_1,
      ADDRESS_LINE2: ADDRESS_LINE_2,
      ADDRESS_LINE3: ADDRESS_LINE_3,
      ADDRESS_LINE4: ADDRESS_LINE_4,
      ...oldData
    } = this.data;
    var details: any = {
      ...oldData,
      ADDRESS_LINE_1,
      ADDRESS_LINE_2,
      ADDRESS_LINE_3,
      ADDRESS_LINE_4,
      EMAIL_ADDRESS: this.emailTxt.value?.trim(),
      FIRST_NAME: this.firstNameTxt.value?.trim(),
      LAST_NAME: this.lastNameTxt.value?.trim(),
      USER_NAME: this.usernameTxt.value?.trim(),
      USER_PROFILES: this.profileMulti.selectedOptions,
      COMPANY_ID: this.isCascadeAdmin
        ? this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
        : this.companyElement.value,
    };

    if (!this.editing) {
      details = {
        ...details,
        ONE_TIME_PASSWORD: this.passwordTxt.value.trim(),
      };
    }
    this.connect.commitEvent(this.event, { DETAILS: details }).then((response) => {
      if (response.MESSAGE_TYPE == 'EVENT_ACK') {
        this.closeModal();
      } else {
        notifyError(response?.ERROR[0]?.CODE, response?.ERROR[0]?.TEXT);
      }
    });
  }

  eventChanged() {
    if (this.usernameTxt && this.event === 'EVENT_INSERT_USER') {
      this.title = 'Insert User';
      this.usernameTxt.disabled = false;
      this.editing = false;
    } else {
      this.title = 'Edit User';
      this.usernameTxt.disabled = true;
      this.editing = true;
      this.loadFields();
    }
  }

  private getProfiles() {
    if (this.data.USER_NAME) {
      this.userService.getProfileByUser(this.data.USER_NAME).then((response) => {
        if (response?.ROW) {
          this.profileMulti.selectedOptions = response.ROW.map((x) => x.PROFILE_NAME);
        } else {
          this.profileMulti.selectedOptions = [];
        }
        if (!this.isCascadeAdmin) {
          this.filterProfileOptions();
        }
      });
    }
  }

  private getAllValidProfiles() {
    this.userService.getAllValidProfiles().then((response) => {
      this.allValidProfiles = response.REPLY;
    });
    this.userService.getAllValidProfileByCompanyRole().then((data) => {
      this.validProfileByCompanyRole = data.REPLY;
    });
  }

  public filterProfileOptions() {
    if (this.profileMulti.selectedOptions.length > 0) {
      this.allProfilesFiltered = this.allProfilesFilteredByCompanyRole.filter((x) =>
        this.checkProfileIsValid(x.PROFILE_NAME),
      );
    } else {
      this.allProfilesFiltered = this.allProfilesFilteredByCompanyRole.filter(
        (x) => x.IS_PRIMARY == true,
      );
    }
    this.profileMulti.options = this.allProfilesFiltered.map((x) => x.PROFILE_NAME);
    this.checkFormCompletionStatus();
  }

  public filterProfilesByCompanyRole() {
    this.profileMulti.selectedOptions = [];
    const company = this.companyList.find((x) => x.CASCADE_COMPANY_ID == this.companyElement.value);

    const validProfilesForCompanyRole = this.validProfileByCompanyRole.filter(
      (x) => x.COMPANY_ROLE === company?.COMPANY_ROLE,
    );
    this.allProfilesFilteredByCompanyRole = this.allProfiles.filter((profile) =>
      validProfilesForCompanyRole.find((x) => x.PROFILE_NAME === profile.PROFILE_NAME),
    );
    this.filterProfileOptions();
    this.checkFormCompletionStatus();
  }

  /**
   * Checks the profile against the already selected profiles in the dropdown.
   * If the profile is not compatible with all the selections then it will not show up in the dropdown.
   * @param profile The profile to check for validity
   * @returns if the profile is valid against all selected profiles in dropdown
   */
  private checkProfileIsValid(profile): boolean {
    //If the profile is already selected inside the dropdown, then it will be valid regardless of if it is compatible with all profiles.
    //This allows the user to unselect a profile causing a conflict
    if (this.profileMulti.selectedOptions.find((x) => x == profile)) {
      return true;
    }

    //Checks the profile against all selected options to see if a record exists in the VALID_PROFILE table
    let isPrimary = this.allProfiles.some((x) => x.PROFILE_NAME === profile && x.IS_PRIMARY);
    if (isPrimary) {
      for (let x of this.profileMulti.selectedOptions) {
        const isValid =
          this.allValidProfiles.find(
            (validCombo) =>
              (validCombo.PROFILE_1 == x && validCombo.PROFILE_2 == profile) ||
              (validCombo.PROFILE_2 == x && validCombo.PROFILE_1 == profile),
            //Checks both possible combinations just in case only one record exists
          ) != null ||
          profile == x ||
          this.allProfiles.some((prof) => prof.PROFILE_NAME === x && !prof.IS_PRIMARY);
        if (!isValid) {
          return false;
        }
      }
      return true;
    } else {
      return this.allValidProfiles.some(
        (validProfile) =>
          (validProfile.PROFILE_1 == profile &&
            this.profileMulti.selectedOptions.some((x) => validProfile.PROFILE_2 == x)) ||
          (validProfile.PROFILE_2 == profile &&
            this.profileMulti.selectedOptions.some((x) => validProfile.PROFILE_1 == x)),
      );
    }
  }

  public checkFormCompletionStatus() {
    this.isFormValid =
      this.usernameTxt.value &&
      ((this.passwordTxt.value && !this.editing) || this.editing) &&
      (this.isCascadeAdmin || this.companyElement.value) &&
      this.profileMulti.selectedOptions?.length > 0;
  }

  private checkCompanySelectionState() {
    this.companyElement.disabled = false;
    if (this.companyElement.value && this.companyList.length === 1) {
      setTimeout(() => {
        this.companyElement.disabled = true;
      }, 100);
    } else if (!this.companyElement.value && this.companyList.length === 1) {
      this.companyElement.value = this.companyList[0].CASCADE_COMPANY_ID;
      setTimeout(() => {
        this.companyElement.disabled = true;
        this.filterProfilesByCompanyRole();
        this.filterProfileOptions();
      }, 100);
    }
  }

  showChanged() {
    if (this.show) {
      this.loadFields();
    } else {
      this.closeModal();
    }
  }

  isCascadeAdminChanged() {
    if (this.profileMulti) {
      this.companyElement.value = '';
      this.profileMulti.selectedOptions = [];
      if (this.isCascadeAdmin) {
        this.profileMulti.options = [Profile.CASCADE_ADMIN];
        this.profileMulti.selectedOptions = [Profile.CASCADE_ADMIN];
      } else {
        this.filterProfilesByCompanyRole();
      }
      this.checkFormCompletionStatus();
    }
  }

  public loadFields() {
    if (this.data && this.data.USER_NAME) {
      this.usernameTxt.value = this.data.USER_NAME;
      this.emailTxt.value = this.data.EMAIL_ADDRESS;
      this.firstNameTxt.value = this.data.FIRST_NAME;
      this.lastNameTxt.value = this.data.LAST_NAME;
      if (
        this.isAdmin &&
        this.data.COMPANY_ID === this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      ) {
        this.isCascadeAdmin = true;
        this.profileMulti.options = [Profile.CASCADE_ADMIN];
      } else {
        this.companyElement.value = this.data.COMPANY_ID;
        this.filterProfilesByCompanyRole();
      }
      this.getProfiles();
      this.checkFormCompletionStatus();
    }
    this.checkCompanySelectionState();
  }

  public closeModal() {
    if (this.profileMulti) {
      this.clearForm();
      this.onCloseCallback();
    }
  }

  public clearForm() {
    this.profileMulti.selectedOptions = [];
    this.allProfilesFiltered = [];
    this.usernameTxt.value = '';
    this.emailTxt.value = '';
    this.firstNameTxt.value = '';
    this.lastNameTxt.value = '';
    this.passwordTxt.value = '';
    this.companyElement.value = '';
    this.isCascadeAdmin = false;
    this.checkCompanySelectionState();
  }
}
